import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled, { css } from "styled-components";
export var InventoryLevelsTable = function(param) {
    var isSynced = param.isSynced, hideInventory = param.hideInventory, showDivider = param.showDivider;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsx(TableContainer, {
            gap: showDivider ? "0" : "2x",
            children: PRODUCTS.map(function(param, index) {
                var imageUrl = param.imageUrl, nameLength = param.nameLength, inventoryCount = param.inventoryCount;
                return /*#__PURE__*/ _jsxs(Column, {
                    children: [
                        /*#__PURE__*/ _jsx(ProductRow, {
                            imageUrl: imageUrl,
                            nameLength: nameLength,
                            inventoryCount: inventoryCount,
                            isSynced: isSynced,
                            hideInventory: hideInventory
                        }),
                        showDivider && index < PRODUCTS.length - 1 ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: "1x"
                                }),
                                /*#__PURE__*/ _jsx(HorizontalLine, {}),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: "1x"
                                })
                            ]
                        }) : null
                    ]
                }, index);
            })
        });
    }
    return /*#__PURE__*/ _jsxs(TableContainer, {
        children: [
            /*#__PURE__*/ _jsxs(HeaderRow, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.subdued,
                        variant: "labelSansMedium",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "ZGmupr",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Status"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.subdued,
                        variant: "labelSansMedium",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "G9gjyB",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Inventory"
                                }
                            ]
                        })
                    })
                ]
            }),
            PRODUCTS.map(function(param, index) {
                var imageUrl = param.imageUrl, nameLength = param.nameLength, inventoryCount = param.inventoryCount;
                return /*#__PURE__*/ _jsx(ProductRow, {
                    imageUrl: imageUrl,
                    nameLength: nameLength,
                    inventoryCount: inventoryCount,
                    isSynced: isSynced
                }, index);
            })
        ]
    });
};
var ProductRow = function(param) {
    var imageUrl = param.imageUrl, nameLength = param.nameLength, inventoryCount = param.inventoryCount, isSynced = param.isSynced, hideInventory = param.hideInventory;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var isSSR = useIsSSR();
    var url = optimizeImageUrl({
        url: imageUrl
    }, isSSR);
    var renderInventory = function() {
        if (hideInventory) {
            return null;
        }
        return /*#__PURE__*/ _jsx(FieldWrapper, {
            $width: getSpacing(isTabletAndBelow ? "4x" : "7x"),
            justify: "flex-end",
            children: isSynced ? /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.subdued,
                variant: "labelSansMedium",
                children: inventoryCount
            }) : /*#__PURE__*/ _jsx(InventoryPlaceholder, {})
        });
    };
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: "2x",
            align: "center",
            children: [
                /*#__PURE__*/ _jsx(ProductImage, {
                    $imageUrl: url
                }),
                /*#__PURE__*/ _jsx(FieldWrapper, {
                    $width: getSpacing("9x"),
                    children: /*#__PURE__*/ _jsx(NameField, {
                        $width: nameLength
                    })
                }),
                /*#__PURE__*/ _jsx(FieldWrapper, {
                    $width: hideInventory ? "fit-content" : getSpacing("9x"),
                    children: /*#__PURE__*/ _jsx(StatusBadge, {
                        isSynced: isSynced,
                        maxWidth: hideInventory ? 136 : 96
                    })
                }),
                renderInventory()
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Column, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsx(HorizontalLine, {}),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsxs(Row, {
                gap: "2x",
                align: "center",
                children: [
                    /*#__PURE__*/ _jsx(ProductImage, {
                        $imageUrl: url
                    }),
                    /*#__PURE__*/ _jsx(FieldWrapper, {
                        $width: getSpacing("10x"),
                        children: /*#__PURE__*/ _jsx(NameField, {
                            $width: nameLength
                        })
                    }),
                    /*#__PURE__*/ _jsx(FieldWrapper, {
                        $width: getSpacing("9x"),
                        children: /*#__PURE__*/ _jsx(StatusBadge, {
                            isSynced: isSynced,
                            maxWidth: 112
                        })
                    }),
                    renderInventory()
                ]
            })
        ]
    });
};
var StatusBadge = function(param) {
    var isSynced = param.isSynced, maxWidth = param.maxWidth;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var badgeContent = isSynced ? strictLocalize({
        id: "sGfzN5",
        defaultMessage: [
            {
                type: 0,
                value: "Synced"
            }
        ]
    }) : strictLocalize({
        id: "rBxNlW",
        defaultMessage: [
            {
                type: 0,
                value: "Syncing"
            }
        ]
    });
    return /*#__PURE__*/ _jsx(BadgeContainer, {
        $isSynced: isSynced,
        $maxWidth: maxWidth,
        children: /*#__PURE__*/ _jsx(BadgeLabel, {
            color: isSynced ? Core.icon.success : Core.icon.warning,
            variant: "labelSansMedium",
            children: badgeContent
        })
    });
};
var PRODUCTS = [
    {
        imageUrl: "https://cdn.faire.com/fastly/4cfccdff3725bebfef0639603591d0207d17dbfa51b94f91a525b57b561945cc.png",
        nameLength: "100%",
        inventoryCount: 3
    },
    {
        imageUrl: "https://cdn.faire.com/fastly/a3b0f8c305c67481d737dfbe3019ebf1284c25b525930b1e24a748ce795dcd36.png",
        nameLength: "60%",
        inventoryCount: 40
    },
    {
        imageUrl: "https://cdn.faire.com/fastly/3f980a75de0f77d9fbbff9ba5562b86eed79305ac47b5486e11ba41fa06bb0bd.png",
        nameLength: "85%",
        inventoryCount: 28
    }
];
var TableContainer = styled(Column).withConfig({
    componentId: "sc-3a4e5c6e-0"
})([
    "height:304px;padding:",
    ";border-radius:4px;background-color:",
    ";box-shadow:0px 4px 10px 0px rgba(0,0,0,0.1);",
    "{height:192px;padding:",
    ";}"
], getSpacing("5x"), Core.surface.inverse, responsiveMediaQueries.tabletAndBelow, getSpacing("3x"));
var ProductImage = styled.div.withConfig({
    componentId: "sc-3a4e5c6e-1"
})([
    "width:",
    ";height:",
    ";border-radius:2px;background-image:",
    ";background-repeat:no-repeat;background-size:cover;background-color:",
    ";flex-shrink:0;",
    "{width:",
    ";height:",
    ";}"
], getSpacing("7x"), getSpacing("7x"), function(props) {
    return props.$imageUrl ? css([
        "url(",
        ")"
    ], props.$imageUrl) : "none";
}, Core.border.muted, responsiveMediaQueries.tabletAndBelow, getSpacing("5x"), getSpacing("5x"));
var HeaderRow = styled(Row).attrs({
    justify: "space-between"
}).withConfig({
    componentId: "sc-3a4e5c6e-2"
})([
    "width:100%;padding-left:168px;"
]);
var FieldWrapper = styled(Row).withConfig({
    componentId: "sc-3a4e5c6e-3"
})([
    "width:",
    ";flex-shrink:0;"
], function(props) {
    return props.$width;
});
var NameField = styled.div.withConfig({
    componentId: "sc-3a4e5c6e-4"
})([
    "width:",
    ";height:12px;background-color:",
    ";border-radius:1px;"
], function(props) {
    return props.$width;
}, Core.border.muted);
var InventoryPlaceholder = styled.div.withConfig({
    componentId: "sc-3a4e5c6e-5"
})([
    "width:",
    ";height:4px;background-color:",
    ";border-radius:4px;"
], getSpacing("2x"), Core.border.muted);
var BadgeContainer = styled.div.withConfig({
    componentId: "sc-3a4e5c6e-6"
})([
    "max-width:",
    "px;display:inline-flex;background-color:",
    ";padding:",
    ";border-radius:4px;"
], function(props) {
    return props.$maxWidth;
}, function(props) {
    return props.$isSynced ? Core.surface.success : Core.surface.warning;
}, getSpacing(4, "1x"));
var BadgeLabel = styled(Typography).withConfig({
    componentId: "sc-3a4e5c6e-7"
})([
    "overflow:hidden;text-overflow:ellipsis;"
]);
